import type {DoppeDtoBymoLink} from './doppe-dto-bymo-link';
import {linkSocialShareSettingsResolveFromUrlMetadata} from './doppe-dto-bymo-link';
import type {DoppeDtoBymoPage, DoppeDtoBymoPageOwnerData, PageSEOSettings,} from './doppe-dto-bymo-page';
import type {DoppeDtoUser} from './doppe-dto-user';
import type {BymoPageStatus} from './bymo-page-status';
import type {DoppeDtoUserSettings} from './doppe-dto-user-settings';
import type {OmitStrict, PartiallyRequired} from '@wix/devzai-utils-common';
import {evaluateFunction} from '@wix/devzai-utils-common';
import type {BymoPageContextProps} from '../../doppe-sdk';
import type {DoppeDtoViewerFeatures} from './doppe-dto-viewer-features';
import type {LinkSocialShareSettingInPageProps, LinkSocialShareSettings} from './link-social-share-settings';
import type {DoppeBlockingStatus} from './doppe-blocking-reason';
import type {DoppePremiumFeaturesStatus} from '../doppe-premium';
import type {BymoPagePropsForViewer} from '../bymo-pages/bymo-page-props';
import {bymoPagePropsGetInnerNavigatablePageBySlug} from '../bymo-pages/bymo-page-props';
import type {DoppeDtoFaviconSettings} from './doppe-dto-favicon-settings';
import {BymoTemplatesIds} from '../doppe-templates/doppe-templates';
import type {DoppeViewerPageInfo} from "../viewer-pages/doppe-viewer-pages";
import {
    doppeViewerPageInfoIsBlogPostsList,
    doppeViewerPageInfoIsContentPage,
    doppeViewerPageInfoIsEventsList,
    doppeViewerPageInfoIsHome,
    doppeViewerPageInfoIsProductsList,
    doppeViewerPageInfoIsServicesList
} from "../viewer-pages/doppe-viewer-pages";
import type {DoppeDtoInnerPage} from './doppe-dto-inner-page';
import {doppeDtoInnerPageIsContent, doppeDtoInnerPageIsVerticalList} from './doppe-dto-inner-page';
import {doppeHideableValueIsVisible} from './doppe-hideable-value';

export type DoppeDtoBymoLinkViewerData = {
    bymoPagePropsOverrides: DoppeDtoBymoLink['bymoPageProps'];
    targetUrl: string | null;
    targetUrlMetadata: DoppeDtoBymoLink['targetUrlMetadata'];
    openGraphTitle: DoppeDtoBymoLink['openGraphTitle'];
    openGraphImage: DoppeDtoBymoLink['openGraphImage'];
    socialShareSettings: DoppeDtoBymoLink['socialShareSettings'];
    pageData?: DoppeDtoBymoLinkViewerData.PageData;
    userAccountName: DoppeDtoUser['subdomain'];
    userDisplayName: DoppeDtoUser['displayName'];
    userProfilePicture: DoppeDtoUser['profilePicture'];
    userPrivacyPolicyUrl?: DoppeDtoUserSettings['privacyPolicyUrl'];
    userTermsOfServiceUrl?: DoppeDtoUserSettings['termsOfServiceUrl'];
    userGoogleAnalyticsSettings?: DoppeDtoUserSettings['googleAnalyticsSettings'];
    userCookieBannerSettings?: DoppeDtoUserSettings['cookieBannerSettings'];
    bymoPageStatus: BymoPageStatus;
    linkId: DoppeDtoBymoLink['id'];
    bymoPageId?: DoppeDtoBymoPage.Id;
    doppeUserId: DoppeDtoUser['id'];
    creatorsMetaSiteId: string | null;
    linkedMetaSiteId: string | null;
    bymoPageContextProps: Partial<BymoPageContextProps>;
    viewerFeatures: Required<DoppeDtoViewerFeatures>;
    isBlocked: DoppeBlockingStatus;
};

export namespace DoppeDtoBymoLinkViewerData {

    export type WithTargetUrl = OmitStrict<DoppeDtoBymoLinkViewerData, 'targetUrl'> & {
        targetUrl: Exclude<DoppeDtoBymoLinkViewerData['targetUrl'], null>
    }

    export type WithConnectedPage = OmitStrict<PartiallyRequired<DoppeDtoBymoLinkViewerData, 'bymoPageId' | 'pageData'>, 'bymoPageContextProps'> & {
        seoSettings: PageSEOSettings;
        bymoPageContextProps: PartiallyRequired<DoppeDtoBymoLinkViewerData['bymoPageContextProps'], 'htmlSiteInfo'>;
        viewerPageInfo: DoppeViewerPageInfo;
        innerPagesBasePath: string;
    };

    export type WithoutConnectedPage = OmitStrict<DoppeDtoBymoLinkViewerData, 'bymoPageId' | 'pageData' | 'bymoPageStatus'> & {
        bymoPageStatus: typeof BymoPageStatus.Disabled
    };

    export type LivePageData = Pick<WithConnectedPage, 'linkId' | 'bymoPageId' | 'doppeUserId'> & {
        pageType: 'pre-roll' | 'speed-dial' | 'action-page'
    }

    export interface PageData {
        bymoPageProps: BymoPagePropsForViewer;
        bymoTemplateId: DoppeDtoBymoPage['bymoTemplateId'];
        ownerData: DoppeDtoBymoPageOwnerData;
        premiumFeaturesStatus: DoppePremiumFeaturesStatus | null;
        pageCachingEnabled: boolean;
        faviconSettings: DoppeDtoFaviconSettings;
    }
}

type NormalizedLinkSocialShareSettings = OmitStrict<LinkSocialShareSettings, 'socialImage' | 'twitterSocialImage' | 'twitterViewType'> & {
    socialImage?: Exclude<LinkSocialShareSettings['socialImage'], null>;
    twitterSocialImage?: Exclude<LinkSocialShareSettings['twitterSocialImage'], null>;
    twitterViewType: Exclude<LinkSocialShareSettings['twitterViewType'], undefined>
}

export function doppeDtoBymoLinkViewerDataResolvePageType (bymoLinkViewerData: DoppeDtoBymoLinkViewerData.WithConnectedPage) {
    if (bymoLinkViewerData.targetUrl) {
        return 'pre-roll' as const;
    } else {
        if (bymoLinkViewerData.pageData.bymoTemplateId === BymoTemplatesIds.SpeedDial) {
            return 'speed-dial' as const;
        } else {
            return 'action-page' as const;
        }
    }
}

export function doppeDtoBymoLinkViewerDataResolveSocialSettings (bymoLinkViewerData: DoppeDtoBymoLinkViewerData.WithConnectedPage) : NormalizedLinkSocialShareSettings {
    const targetUrlMetadata = bymoLinkViewerData.targetUrlMetadata;

    const socialShareSettingsFromUrlMetadata = targetUrlMetadata ? linkSocialShareSettingsResolveFromUrlMetadata(targetUrlMetadata) : {};

    return linkSocialShareSettingsNormalizeSettingsValues({
        ...socialShareSettingsFromUrlMetadata,
        ...bymoLinkViewerData.socialShareSettings
    })
}

export function linkSocialShareSettingsNormalizeSettingsValues (linkSocialShareSettings: LinkSocialShareSettings) : NormalizedLinkSocialShareSettings {
    return {
        twitterViewType: 'large',
        ...linkSocialShareSettings,
        title: linkSocialShareSettingsNormalizeStringSettingValue(linkSocialShareSettings.title),
        twitterTitle: linkSocialShareSettingsNormalizeStringSettingValue(linkSocialShareSettings.twitterTitle),
        description: linkSocialShareSettingsNormalizeStringSettingValue(linkSocialShareSettings.description),
        twitterDescription: linkSocialShareSettingsNormalizeStringSettingValue(linkSocialShareSettings.twitterDescription),
        socialImage: linkSocialShareSettings.socialImage === null ? undefined : linkSocialShareSettings.socialImage,
        twitterSocialImage: linkSocialShareSettings.twitterSocialImage === null ? undefined : linkSocialShareSettings.twitterSocialImage,
    }
}

function linkSocialShareSettingsNormalizeStringSettingValue (value: string | undefined) {
    if (value !== undefined) {
        const normalizedValue = value.trim();

        return normalizedValue === '' ? undefined : normalizedValue;
    } else {
        return undefined;
    }
}

export function doppeDtoBymoLinkViewerDataIsWithConnectedPage (bymoLinkViewerData: DoppeDtoBymoLinkViewerData) : bymoLinkViewerData is DoppeDtoBymoLinkViewerData.WithConnectedPage {
    return bymoLinkViewerData.bymoPageId !== undefined;
}

export function doppeDtoBymoLinkViewerDataIsWithTargetUrl (bymoLinkViewerData: DoppeDtoBymoLinkViewerData) : bymoLinkViewerData is DoppeDtoBymoLinkViewerData.WithTargetUrl {
    return bymoLinkViewerData.targetUrl !== null;
}

/**
 * Resolves the inner page with settings (socialShareSettings, seoSettings) based on the current viewerPageInfo.
 * Returns null for home page or if the inner page doesn't exist.
 */
export function doppeDtoBymoLinkViewerDataResolveCurrentInnerPageWithSettings(
    bymoLinkViewerData: DoppeDtoBymoLinkViewerData.WithConnectedPage
): (DoppeDtoInnerPage.Content | DoppeDtoInnerPage.Products | DoppeDtoInnerPage.BlogPosts | DoppeDtoInnerPage.Events | DoppeDtoInnerPage.Bookings) | null {
    const viewerPageInfo = bymoLinkViewerData.viewerPageInfo;
    const bymoPageProps = bymoLinkViewerData.pageData.bymoPageProps;

    // Home page - no inner page
    if (doppeViewerPageInfoIsHome(viewerPageInfo)) {
        return null;
    }

    // Content page - find by slug
    if (doppeViewerPageInfoIsContentPage(viewerPageInfo)) {
        const innerPage = bymoPagePropsGetInnerNavigatablePageBySlug(bymoPageProps, viewerPageInfo.slug);
        if (innerPage && doppeDtoInnerPageIsContent(innerPage)) {
            return innerPage;
        }
        return null;
    }

    // Vertical list pages - use the default page settings
    if (doppeViewerPageInfoIsProductsList(viewerPageInfo)) {
        return bymoPageProps.defaultStoreProductsPage;
    }

    if (doppeViewerPageInfoIsBlogPostsList(viewerPageInfo)) {
        return bymoPageProps.defaultBlogPostsPage;
    }

    if (doppeViewerPageInfoIsEventsList(viewerPageInfo)) {
        return bymoPageProps.defaultEventsPage;
    }

    if (doppeViewerPageInfoIsServicesList(viewerPageInfo)) {
        return bymoPageProps.defaultBookingsPage;
    }

    // Item pages (product, blog post, event, service) - no socialShareSettings/seoSettings yet
    return null;
}

/**
 * Converts LinkSocialShareSettingInPageProps (with hideable values) to LinkSocialShareSettings (plain values).
 * Only returns visible values that are explicitly set.
 */
function linkSocialShareSettingsInPagePropsConvertToLinkSocialShareSettings(
    pageSettings: LinkSocialShareSettingInPageProps
): LinkSocialShareSettings {
    return {
        title: doppeHideableValueIsVisible(pageSettings.title) ? pageSettings.title : undefined,
        description: doppeHideableValueIsVisible(pageSettings.description) ? pageSettings.description : undefined,
        socialImage: doppeHideableValueIsVisible(pageSettings.socialImage) ? pageSettings.socialImage : undefined,
        twitterTitle: doppeHideableValueIsVisible(pageSettings.twitterTitle) ? pageSettings.twitterTitle : undefined,
        twitterDescription: doppeHideableValueIsVisible(pageSettings.twitterDescription) ? pageSettings.twitterDescription : undefined,
        twitterSocialImage: doppeHideableValueIsVisible(pageSettings.twitterSocialImage) ? pageSettings.twitterSocialImage : undefined,
        twitterViewType: doppeHideableValueIsVisible(pageSettings.twitterViewType) ? pageSettings.twitterViewType : undefined,
    };
}

/**
 * Resolves social share settings for the current page (inner page or main page).
 * If the current page is an inner page with socialShareSettings, those settings are used.
 * Falls back to the main page's socialShareSettings if inner page doesn't have custom settings.
 */
export function doppeDtoBymoLinkViewerDataResolveSocialSettingsForCurrentPage(
    bymoLinkViewerData: DoppeDtoBymoLinkViewerData.WithConnectedPage
): NormalizedLinkSocialShareSettings {
    const targetUrlMetadata = bymoLinkViewerData.targetUrlMetadata;
    const socialShareSettingsFromUrlMetadata = targetUrlMetadata ? linkSocialShareSettingsResolveFromUrlMetadata(targetUrlMetadata) : {};

    // Try to get inner page settings
    const innerPage = doppeDtoBymoLinkViewerDataResolveCurrentInnerPageWithSettings(bymoLinkViewerData);
    
    const innerPageSocialShareSettings = evaluateFunction(() => {
        if (!innerPage) {
            return {};
        }

        // Content pages and vertical list pages have socialShareSettings
        if ((doppeDtoInnerPageIsContent(innerPage) || doppeDtoInnerPageIsVerticalList(innerPage)) && innerPage.socialShareSettings) {
            return linkSocialShareSettingsInPagePropsConvertToLinkSocialShareSettings(innerPage.socialShareSettings);
        }

        return {};
    });

    // Merge: urlMetadata -> main page settings -> inner page settings (inner page takes precedence)
    return linkSocialShareSettingsNormalizeSettingsValues({
        ...socialShareSettingsFromUrlMetadata,
        ...bymoLinkViewerData.socialShareSettings,
        ...innerPageSocialShareSettings,
    });
}

/**
 * Resolves SEO settings for the current page (inner page or main page).
 * If the current page is an inner page with seoSettings, those settings are used.
 * Falls back to the main page's seoSettings.
 */
export function doppeDtoBymoLinkViewerDataResolveSeoSettingsForCurrentPage(
    bymoLinkViewerData: DoppeDtoBymoLinkViewerData.WithConnectedPage
): PageSEOSettings {
    const mainPageSeoSettings = bymoLinkViewerData.seoSettings;

    // Try to get inner page settings
    const innerPage = doppeDtoBymoLinkViewerDataResolveCurrentInnerPageWithSettings(bymoLinkViewerData);
    
    if (!innerPage) {
        return mainPageSeoSettings;
    }

    // Content pages and vertical list pages have seoSettings
    if ((doppeDtoInnerPageIsContent(innerPage) || doppeDtoInnerPageIsVerticalList(innerPage)) && innerPage.seoSettings) {
        const innerPageSeoSettings = innerPage.seoSettings;
        
        // Convert hideable values to plain values, falling back to main page settings
        const title = doppeHideableValueIsVisible(innerPageSeoSettings.title)
            ? innerPageSeoSettings.title
            : mainPageSeoSettings.title;
        
        const description = doppeHideableValueIsVisible(innerPageSeoSettings.description)
            ? innerPageSeoSettings.description
            : mainPageSeoSettings.description;
        
        const indexType = innerPageSeoSettings.indexType ?? mainPageSeoSettings.indexType;

        return {
            title: title,
            description: description,
            indexType: indexType,
        };
    }

    return mainPageSeoSettings;
}